// src/pages/ExecutarChecklist.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import MainLayout from "../components/mainLayout";
import { api } from "../api";
import { showError, showInfo } from "../utils/toast";
import SelectCliente from "../components/ui/selectCliente";
import "../sass/pages/execChecklist.scss";

/* ========== Primitives ========== */
const Button = ({ children, variant = "primary", className = "", ...props }) => (
  <button className={`ssw-btn ssw-btn--${variant} ${className}`} {...props}>
    {children}
  </button>
);
const Input = ({ className = "", ...props }) => (
  <input {...props} className={`ssw-input ${className}`} />
);
const Select = ({ className = "", ...props }) => (
  <select {...props} className={`ssw-select ${className}`} />
);
const Textarea = ({ className = "", ...props }) => (
  <textarea {...props} className={`ssw-textarea ${className}`} />
);

/* ========== Helpers ========== */
const yesNo = [
  { v: "", label: "—" },
  { v: "true", label: "Sim" },
  { v: "false", label: "Não" },
];

const normStr = (v) => (v == null ? "" : String(v).trim());

const SECTION_PREFIX = "__SSW_SECTION__::";

const isSectionQuestion = (pergunta = {}) =>
  normStr(pergunta?.Enunciado ?? pergunta?.enunciado).startsWith(SECTION_PREFIX);

const getSectionTitle = (pergunta = {}) => {
  const texto = normStr(pergunta?.Enunciado ?? pergunta?.enunciado);
  return isSectionQuestion(pergunta)
    ? texto.slice(SECTION_PREFIX.length).trim()
    : "";
};

const normalizeBool = (v) =>
  v === true || v === "true" || v === 1
    ? true
    : v === false || v === "false" || v === 0
      ? false
      : null;

const fmtDateTime = (iso) =>
  new Date(iso).toLocaleString("pt-BR", { hour12: false });

const getLoggedUserName = () => {
  try {
    const get = (k) =>
      (sessionStorage.getItem(k) ?? localStorage.getItem(k) ?? "").trim();
    const direct = get("RazaoSocial") || get("NomeFunc") || get("name");
    if (direct) return direct;
    const parse = (raw) => {
      try {
        return JSON.parse(raw || "{}");
      } catch {
        return {};
      }
    };
    const u = {
      ...parse(sessionStorage.getItem("user")),
      ...parse(localStorage.getItem("user")),
    };
    return (u.RazaoSocial || u.nome || u.name || u.NomeFunc || "")
      .toString()
      .trim();
  } catch {
    return "";
  }
};

const getLoggedUserId = () => {
  const candidates = [
    sessionStorage.getItem("codFunc"),
    sessionStorage.getItem("CodCadFunc"),
    sessionStorage.getItem("id"),
  ];
  for (const c of candidates) {
    const n = Number(c);
    if (Number.isInteger(n) && n > 0) return n;
  }
  return 0;
};

const friendlyErr = (e, fallback = "Falha na operação.") => {
  const msgApi =
    e?.response?.data?.mensagem || e?.response?.data?.error || e?.message;
  const status = e?.response?.status;
  if (status)
    return `${fallback} (HTTP ${status})${msgApi ? ` — ${msgApi}` : ""}`;
  return msgApi ? `${fallback} — ${msgApi}` : fallback;
};

const isCancelLike = (error) => {
  return (
    error?.name === "CanceledError" ||
    error?.code === "ERR_CANCELED" ||
    error?.name === "AbortError"
  );
};

const normalizeCliente = (cli = {}) => {
  const get = (...ks) => {
    for (const k of ks) {
      const v = cli?.[k];
      if (v != null && String(v).trim() !== "") return String(v).trim();
    }
    return "";
  };

  const CodigoCad = get(
    "CodigoCad",
    "codigoCad",
    "codigo",
    "ChvCad",
    "chvCad",
    "CODIGO_CAD",
    "value"
  );

  const RazaoSocial = get(
    "RazaoSocial",
    "RAZAO_SOCIAL",
    "razao",
    "razaoSocial",
    "Razao",
    "Cliente",
    "Razao_Social",
    "NOME_RAZAO",
    "nome"
  );

  const NomeFant = get(
    "NomeFant",
    "NomeFantasia",
    "NOME_FANTASIA",
    "nomeFantasia",
    "fantasia",
    "ClienteFantasia"
  );

  return {
    ...cli,
    CodigoCad,
    RazaoSocial,
    NomeFant,
  };
};

const buildClienteLabel = (codigo, data, fallbackLabel) => {
  const c = normStr(codigo);
  const nomeFant =
    normStr(data?.NomeFant) ||
    normStr(data?.NomeFantasia) ||
    normStr(data?.fantasia);

  const razao =
    normStr(data?.RazaoSocial) ||
    normStr(data?.razao) ||
    normStr(data?.nome) ||
    normStr(data?.RAZAO_SOCIAL);

  const base = nomeFant || razao || fallbackLabel || c || "Cliente";
  return c ? `${c} - ${base}` : base;
};

/* ========== Serviços REST (execução/respostas) ========== */
// Lista só templates cujo cabeçalho está Ativo = 1.
// A versão usada vem do campo Versao, que é atualizado ao publicar uma versão.
async function apiListarTemplatesAtivos() {
  const { data } = await api.get("/validacao/templates", {
    params: { ativo: 1 },
  });

  const arr = Array.isArray(data?.data)
    ? data.data
    : Array.isArray(data)
      ? data
      : [];

  return arr.map((t) => ({
    Id: Number(t.Id ?? t.id ?? t.ID) || null,
    Titulo: String(t.Titulo ?? t.titulo ?? "").trim(),
    Versao: Number(t.Versao ?? t.versao ?? 1) || 1,
    VersaoAtivaId: null,
    Versoes: [], // não precisa da lista de versões pra executar
  }));
}

async function apiCriarExecucao(payload) {
  const { data } = await api.post("/validacao/execucoes", payload);
  return data;
}
async function apiObterExecucao(execucaoId) {
  const { data } = await api.get(`/validacao/execucoes/${execucaoId}`);
  return data;
}
async function apiUpsertRespostas(execucaoId, respostas) {
  const { data } = await api.post(
    `/validacao/execucoes/${execucaoId}/respostas`,
    { respostas }
  );
  return data;
}
async function apiAtualizarStatus(execucaoId, status) {
  const { data } = await api.patch(`/validacao/execucoes/${execucaoId}/status`, {
    status,
  });
  return data;
}
async function apiAssinar(execucaoId, payload) {
  const { data } = await api.post(
    `/validacao/execucoes/${execucaoId}/assinar`,
    payload
  );
  return data;
}
async function apiBuscarClientePorCodigo(codigoCad) {
  const codigo = normStr(codigoCad);
  if (!codigo) return null;
  try {
    const { data } = await api.get("/cliente/por-codigo", {
      params: { codigo },
    });
    return data || null;
  } catch {
    return null;
  }
}

/* ========== Serviços REST (imagens) ========== */
async function apiListarImagens(execucaoId, perguntaId = null) {
  const { data } = await api.get(`/validacao/execucoes/${execucaoId}/imagens`, {
    params: perguntaId != null ? { pergunta_id: perguntaId } : undefined,
  });
  return Array.isArray(data) ? data : Array.isArray(data?.data) ? data.data : [];
}

async function apiUploadImagens(execucaoId, files, perguntaId = null) {
  const fd = new FormData();
  for (const f of files) fd.append("files", f);
  if (perguntaId != null) fd.append("pergunta_id", String(perguntaId));
  const { data } = await api.post(
    `/validacao/execucoes/${execucaoId}/imagens`,
    fd,
    { headers: { "Content-Type": "multipart/form-data" } }
  );
  return data;
}

async function apiExcluirImagem(execucaoId, midiaId) {
  const { data } = await api.delete(
    `/validacao/execucoes/${execucaoId}/imagens/${midiaId}`
  );
  return data;
}

/* NOVO: lista execuções em aberto do cliente */
async function apiListarExecucoesAbertasDoCliente(clienteId) {
  if (!clienteId) return [];
  try {
    const { data } = await api.get("/validacao/execucoes", {
      params: { cliente_id: clienteId, status: "em_aberto", limit: 20, offset: 0 },
    });
    return Array.isArray(data) ? data : [];
  } catch (e) {
    showError(friendlyErr(e, "Falha ao listar execuções em aberto do cliente."));
    return [];
  }
}

/* NOVO: histórico por template (última execução do cliente para cada template) */
async function apiListarHistoricoCliente(clienteId) {
  if (!clienteId) return [];
  try {
    const { data } = await api.get("/validacao/aprovacoes", {
      params: { status: "Todos", cliente_id: clienteId, limit: 200, offset: 0 },
    });
    const rows = Array.isArray(data) ? data : [];
    const byTemplate = new Map();
    for (const r of rows) {
      const key = r.TemplateId ?? r.Titulo;
      const prev = byTemplate.get(key);
      const currTime = r.DtFim ? new Date(r.DtFim).getTime() : 0;
      const prevTime = prev?.DtFim ? new Date(prev.DtFim).getTime() : -1;
      if (
        !prev ||
        currTime > prevTime ||
        (currTime === prevTime && (r.Id || 0) > (prev.Id || 0))
      ) {
        byTemplate.set(key, r);
      }
    }
    return Array.from(byTemplate.values()).sort((a, b) => {
      const ta = a.DtFim ? new Date(a.DtFim).getTime() : 0;
      const tb = b.DtFim ? new Date(b.DtFim).getTime() : 0;
      return tb - ta;
    });
  } catch (e) {
    showError(friendlyErr(e, "Falha ao obter histórico do cliente."));
    return [];
  }
}

/* ========== Componentes auxiliares (UI Anexos) ========== */
function ImgThumb({ item, onDelete }) {
  return (
    <div className="ssw-imgthumb">
      <div className="ssw-imgthumb__icon" title={item.MimeType}>
        🖼️
      </div>
      <div className="ssw-imgthumb__meta">
        <div className="ssw-imgthumb__key" title={item.BucketKey}>
          {item.BucketKey}
        </div>
        <div className="ssw-imgthumb__when">{fmtDateTime(item.UploadedEm)}</div>
      </div>
      <Button variant="ghost" className="ssw-imgthumb__del" onClick={onDelete}>
        Excluir
      </Button>
    </div>
  );
}

/* ========== Página ========== */
export default function ExecutarChecklist() {
  // Templates e versões
  const [templates, setTemplates] = useState([]);
  const [templateId, setTemplateId] = useState("");
  const [templateVersoes, setTemplateVersoes] = useState([]); // [{Id, Numero, Status}]
  const [versionId, setVersionId] = useState(""); // versão selecionada (quando existir)

  // Cliente (NOVO SelectCliente controlado)
  const [clienteSel, setClienteSel] = useState(null); // dados normalizados
  const [clienteOption, setClienteOption] = useState(null); // option do SelectCliente

  const clienteId = useMemo(() => {
    const c = normStr(clienteSel?.CodigoCad);
    const n = Number(c);
    return Number.isFinite(n) && n > 0 ? n : "";
  }, [clienteSel]);

  // Responsável
  const [responsavelId, setResponsavelId] = useState(
    String(getLoggedUserId() || sessionStorage.getItem("codFunc") || "")
  );
  const [responsavelNome] = useState(getLoggedUserName());

  // Execução
  const [execId, setExecId] = useState(null); // ID real da execução
  const [execIdInput, setExecIdInput] = useState(""); // input (retomar por id)
  const [loading, setLoading] = useState(false);
  const [execucao, setExecucao] = useState(null);
  const [perguntas, setPerguntas] = useState([]);
  const [respostas, setRespostas] = useState({});
  const [assinatura, setAssinatura] = useState(null);
  const [aceite, setAceite] = useState(false);

  // Abertas + histórico
  const [abertasCliente, setAbertasCliente] = useState([]);
  const [loadingAbertas, setLoadingAbertas] = useState(false);
  const [historicoCliente, setHistoricoCliente] = useState([]);
  const [loadingHistorico, setLoadingHistorico] = useState(false);

  // Imagens
  const [imagens, setImagens] = useState({});
  const [uploadBusy, setUploadBusy] = useState({});
  const [listBusy, setListBusy] = useState({});
  const [delBusy, setDelBusy] = useState({});

  // Abort de carregamento pesado (troca rápida de exec)
  const loadSeq = useRef(0);

  // Boot templates
  useEffect(() => {
    (async () => {
      try {
        const list = await apiListarTemplatesAtivos();
        setTemplates(list);
      } catch (e) {
        showError(friendlyErr(e, "Falha ao carregar templates."));
      }
    })();
  }, []);

  // Ao escolher template, configurar versões
  useEffect(() => {
    const t = templates.find((x) => String(x.Id) === String(templateId));
    if (!t) {
      setTemplateVersoes([]);
      setVersionId("");
      return;
    }
    setTemplateVersoes(t.Versoes || []);
    const def =
      (t.Versoes || []).find((v) => v.Status === "ACTIVE") ||
      (t.Versoes || [])[0] ||
      null;
    setVersionId(def?.Id ? String(def.Id) : "");
  }, [templateId, templates]);

  // NOVO: handler do SelectCliente
  const handleSelectCliente = (opt) => {
    if (!opt) {
      setClienteOption(null);
      setClienteSel(null);
      setAbertasCliente([]);
      setHistoricoCliente([]);
      return;
    }

    setClienteOption(opt);

    const raw = opt?.data ?? opt;
    const norm = normalizeCliente(raw);
    const codigo = normStr(norm.CodigoCad) || normStr(opt?.value);

    const sel = normalizeCliente({ ...raw, ...norm, CodigoCad: codigo });
    setClienteSel(sel);
  };

  // Quando escolher cliente sem execução carregada, listar abertas/histórico
  useEffect(() => {
    if (!clienteId || execId) return;

    let canceled = false;
    (async () => {
      setLoadingAbertas(true);
      setLoadingHistorico(true);
      const [rowsAbertas, rowsHist] = await Promise.all([
        apiListarExecucoesAbertasDoCliente(clienteId),
        apiListarHistoricoCliente(clienteId),
      ]);
      if (canceled) return;
      setAbertasCliente(rowsAbertas);
      setHistoricoCliente(rowsHist);
      setLoadingAbertas(false);
      setLoadingHistorico(false);
    })();

    return () => {
      canceled = true;
    };
  }, [clienteId, execId]);

  /* Computados */
  const perguntasRespondiveis = useMemo(
    () => perguntas.filter((p) => !isSectionQuestion(p)),
    [perguntas],
  );

  const ordemVisualPorId = useMemo(() => {
    const mapa = new Map();
    let numero = 0;

    for (const p of perguntas) {
      if (isSectionQuestion(p)) continue;
      numero += 1;
      mapa.set(p.Id, numero);
    }

    return mapa;
  }, [perguntas]);

  const obrigatorias = useMemo(
    () => perguntasRespondiveis.filter((p) => p.Obrigatoria && p.Ativa),
    [perguntasRespondiveis],
  );

  const respondidas = useMemo(() => {
    const setIds = new Set(Object.keys(respostas).map((x) => Number(x)));
    return perguntasRespondiveis.filter(
      (p) => setIds.has(p.Id) && respostas[p.Id]?.bool !== null,
    );
  }, [perguntasRespondiveis, respostas]);

  const pendentesObrig = useMemo(() => {
    const map = new Map(Object.entries(respostas));
    return obrigatorias.filter(
      (p) => !map.has(String(p.Id)) || map.get(String(p.Id))?.bool === null
    );
  }, [obrigatorias, respostas]);

  const locked = Boolean(assinatura);

  /* ===== Imagens ===== */
  function setPidListBusy(pid, v) {
    setListBusy((prev) => ({ ...prev, [pid]: !!v }));
  }
  function setPidUploadBusy(pid, v) {
    setUploadBusy((prev) => ({ ...prev, [pid]: !!v }));
  }
  function setMidiaDelBusy(mid, v) {
    setDelBusy((prev) => ({ ...prev, [mid]: !!v }));
  }

  async function carregarImagensPergunta(pid) {
    if (!execId) return;
    setPidListBusy(pid, true);
    try {
      const list = await apiListarImagens(execId, pid);
      setImagens((prev) => ({ ...prev, [pid]: list }));
    } catch (e) {
      showError(friendlyErr(e, "Falha ao listar imagens."));
    } finally {
      setPidListBusy(pid, false);
    }
  }

  async function excluirImagem(pid, midiaId) {
    if (!execId || !midiaId) return;
    setMidiaDelBusy(midiaId, true);
    try {
      const r = await apiExcluirImagem(execId, midiaId);
      if (r?.sucesso) {
        setImagens((prev) => ({
          ...prev,
          [pid]: (prev[pid] || []).filter((x) => x.Id !== midiaId),
        }));
        showInfo("Imagem excluída.");
      } else {
        showError("Falha ao excluir imagem.");
      }
    } catch (e) {
      showError(friendlyErr(e, "Falha ao excluir imagem."));
    } finally {
      setMidiaDelBusy(midiaId, false);
    }
  }

  async function uploadImagensPergunta(pid, fileList) {
    if (!execId || !fileList?.length) return;

    const files = Array.from(fileList);
    const ALLOWED = new Set(["image/jpeg", "image/png", "image/webp"]);

    for (const f of files) {
      if (!ALLOWED.has(f.type)) {
        showError(`Arquivo inválido (${f.name}). Tipos permitidos: JPG, PNG, WEBP.`);
        return;
      }
      if (f.size > 10 * 1024 * 1024) {
        showError(`Arquivo muito grande (${f.name}). Máximo 10MB.`);
        return;
      }
    }

    setPidUploadBusy(pid, true);
    try {
      const r = await apiUploadImagens(execId, files, pid);
      if (r?.sucesso) {
        showInfo(`Upload concluído (${r.total} imagem${r.total > 1 ? "s" : ""}).`);
        await carregarImagensPergunta(pid);
      } else {
        showError(r?.mensagem || "Falha no upload.");
      }
    } catch (e) {
      showError(friendlyErr(e, "Falha no upload."));
    } finally {
      setPidUploadBusy(pid, false);
    }
  }

  /* ===== Execução/Respostas ===== */
  async function handleNovaExecucao() {
    if (!templateId) return showError("Selecione um template.");
    if (!clienteId) return showError("Selecione o cliente alvo.");

    const payload = {
      cliente_id: Number(clienteId),
      responsavel_user_id: responsavelId ? Number(responsavelId) : null,
      dt_agendada: null,
    };
    if (versionId) payload.version_id = Number(versionId);
    else payload.template_id = Number(templateId);

    setLoading(true);
    try {
      const r = await apiCriarExecucao(payload);
      if (!r?.sucesso) return showError("Não foi possível abrir a execução.");
      await handleCarregarExecucao(r.execucao_id);
      showInfo("Execução criada.");
    } catch (e) {
      showError(friendlyErr(e, "Falha ao criar execução."));
    } finally {
      setLoading(false);
    }
  }

  async function handleCarregarExecucao(id) {
    const seq = ++loadSeq.current;

    setLoading(true);
    try {
      const data = await apiObterExecucao(id);
      if (seq !== loadSeq.current) return;

      const head = data.execucao;
      const pergs = Array.isArray(data.perguntas) ? data.perguntas : [];
      const resps = Array.isArray(data.respostas) ? data.respostas : [];

      setExecId(Number(id));
      setExecIdInput("");
      setExecucao(head);
      setAssinatura(data.assinatura || null);
      setPerguntas(pergs);

      const map = {};
      for (const r of resps) {
        map[r.PerguntaId] = {
          bool: normalizeBool(r.RespostaBool),
          comentario: r.Comentario ?? "",
        };
      }
      setRespostas(map);

      setTemplateId(String(head.TemplateId));
      if (head.VersionId) setVersionId(String(head.VersionId));

      // Cliente (NOVO: popula option + sel)
      const cliRaw = await apiBuscarClientePorCodigo(head.ClienteId);
      if (seq !== loadSeq.current) return;

      const cliNorm = normalizeCliente(cliRaw || { CodigoCad: head.ClienteId });
      const codigo = normStr(cliNorm.CodigoCad) || normStr(head.ClienteId);

      setClienteSel({ ...cliNorm, CodigoCad: codigo });

      setClienteOption({
        value: codigo,
        label: buildClienteLabel(codigo, cliNorm, ""),
        data: { ...cliNorm, CodigoCad: codigo },
      });

      // imagens por pergunta
      const newImgs = {};
      for (const p of pergs.filter((item) => !isSectionQuestion(item))) {
        try {
          newImgs[p.Id] = await apiListarImagens(id, p.Id);
        } catch (e) {
          if (isCancelLike(e)) return;
          newImgs[p.Id] = [];
        }
        if (seq !== loadSeq.current) return;
      }
      setImagens(newImgs);

      setAbertasCliente([]);
      setHistoricoCliente([]);
    } catch (e) {
      if (!isCancelLike(e)) showError(friendlyErr(e, "Execução não encontrada."));
    } finally {
      if (seq === loadSeq.current) setLoading(false);
    }
  }

  // Carregar via ?exec=ID
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const qExec = Number(params.get("exec"));
    if (qExec) handleCarregarExecucao(qExec);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function setRespLocal(pid, patch) {
    setRespostas((prev) => ({
      ...prev,
      [pid]: {
        bool: prev[pid]?.bool ?? null,
        comentario: prev[pid]?.comentario ?? "",
        ...patch,
      },
    }));
  }

  function montarLote() {
    return perguntas.map((p) => {
      // Títulos são persistidos no mesmo contrato das perguntas para manter
      // compatibilidade com o backend atual. Eles recebem resposta técnica
      // automática e nunca entram nos contadores/pendências da interface.
      if (isSectionQuestion(p)) {
        return {
          pergunta_id: p.Id,
          resposta_bool: true,
          comentario: null,
        };
      }

      const r = respostas[p.Id] || { bool: null, comentario: "" };
      return {
        pergunta_id: p.Id,
        resposta_bool: r.bool,
        comentario: r.comentario || null,
      };
    });
  }

  async function handleSalvarRascunho() {
    if (!execId) return showError("Abra ou carregue uma execução.");
    if (locked) return showError("Execução assinada. Edição bloqueada.");

    setLoading(true);
    try {
      const r = await apiUpsertRespostas(execId, montarLote());
      if (r?.sucesso) showInfo("Rascunho salvo.");
      else showError("Falha ao salvar rascunho.");
    } catch (e) {
      showError(friendlyErr(e, "Falha ao salvar rascunho."));
    } finally {
      setLoading(false);
    }
  }

  async function handlePausarRetomar() {
    if (!execId) return;
    if (locked) return showError("Execução assinada. Operação não permitida.");

    const atual = (execucao?.Status || "ABERTO").toUpperCase();
    const next = atual === "PAUSADO" ? "ABERTO" : "PAUSADO";

    setLoading(true);
    try {
      const r = await apiAtualizarStatus(execId, next);
      if (r?.sucesso) {
        setExecucao((h) => ({ ...h, Status: next }));
        showInfo(next === "PAUSADO" ? "Execução pausada." : "Execução retomada.");
      } else {
        showError("Falha ao atualizar status.");
      }
    } catch (e) {
      showError(friendlyErr(e, "Falha ao atualizar status."));
    } finally {
      setLoading(false);
    }
  }

  async function handleAssinar() {
    if (!execId) return;
    if (locked) return showError("Execução já assinada.");
    if (!aceite) return showError("É obrigatório aceitar o termo de responsabilidade.");

    const obrigPend = pendentesObrig;
    if (obrigPend.length > 0) {
      const primeiras = obrigPend
        .slice(0, 3)
        .map((p) => `#${ordemVisualPorId.get(p.Id) ?? p.Ordem}`)
        .join(", ");
      const extra = obrigPend.length > 3 ? `, +${obrigPend.length - 3} outras` : "";
      return showError(
        `Responda todas as obrigatórias antes de assinar. Pendentes: ${primeiras}${extra}.`
      );
    }

    setLoading(true);
    try {
      await apiUpsertRespostas(execId, montarLote());
      const payload = {
        assinante_user_id: Number(responsavelId || getLoggedUserId() || 0) || null,
        aceite: true,
        ip: null,
        user_agent: navigator.userAgent,
      };
      const r = await apiAssinar(execId, payload);
      if (r?.sucesso) {
        showInfo("Checklist assinado e concluído.");
        await handleCarregarExecucao(execId);
      } else {
        showError(r?.mensagem || "Falha na assinatura.");
      }
    } catch (e) {
      showError(friendlyErr(e, "Falha na assinatura."));
    } finally {
      setLoading(false);
    }
  }

  /* Render */
  const answeredCount = respondidas.length;
  const requiredCount = obrigatorias.length;
  const status = (execucao?.Status || (execId ? "ABERTO" : "—")).toUpperCase();

  const assinanteNomeExibicao = (() => {
    if (!assinatura) return "";
    const myId = Number(getLoggedUserId());
    const assinId = Number(assinatura.AssinanteUserId);
    if (myId && assinId && myId === assinId && responsavelNome) return responsavelNome;
    return responsavelNome || `usuário ${assinId || "—"}`;
  })();

  const tplSelecionado = templates.find((t) => String(t.Id) === String(templateId));

  const versaoNumeroSelecionada = execucao
    ? execucao.TemplateVersao ?? null
    : (() => {
      if (!tplSelecionado) return null;
      if (versionId && templateVersoes.length) {
        const v = templateVersoes.find((x) => String(x.Id) === String(versionId));
        if (v && v.Numero != null) return v.Numero;
      }
      return tplSelecionado.Versao ?? null;
    })();

  return (
    <MainLayout>
      <div className="ssw-page-exec-checklist">
        {/* Header */}
        <div className="ssw-header">
          <div>
            <h1>Responder checklist</h1>
            <div className="ssw-sub">
              <span className="ssw-badge is-blue">{execId ? `Execução #${execId}` : "Novo"}</span>
              {execId && <span className="ssw-badge is-gray">Status: {status}</span>}
              {versaoNumeroSelecionada != null && <span className="ssw-badge">v{versaoNumeroSelecionada}</span>}
              <span className="ssw-kpi">
                {answeredCount}/{perguntasRespondiveis.length} respondidas • obrigatórias: {requiredCount}
              </span>
            </div>
          </div>
          <div className="ssw-actions">
            <Button variant="ghost" onClick={() => window.location.reload()}>
              Resetar
            </Button>
          </div>
        </div>

        {/* Abertura / retomada */}
        <div className="ssw-card">
          <div className="ssw-card__head">
            <h2>Abertura / retomada</h2>
          </div>

          {!execId && (
            <>
              <div className="ssw-grid ssw-grid--3">
                <div className="ssw-field">
                  <label>Template *</label>
                  <Select value={templateId} onChange={(e) => setTemplateId(e.target.value)}>
                    <option value="">Selecione…</option>
                    {templates.map((t) => (
                      <option key={t.Id} value={t.Id}>
                        {t.Titulo} (v{t.Versao})
                      </option>
                    ))}
                  </Select>
                </div>

                {templateVersoes.length > 0 && (
                  <div className="ssw-field">
                    <label>Versão (ativa por default)</label>
                    <Select value={versionId} onChange={(e) => setVersionId(e.target.value)}>
                      {templateVersoes.map((v) => (
                        <option key={v.Id} value={v.Id}>
                          v{v.Numero} {v.Status === "ACTIVE" ? "• ATIVA" : ""}
                        </option>
                      ))}
                    </Select>
                  </div>
                )}

                <div className="ssw-field" style={{ gridColumn: "1 / -1" }}>
                  <label>Cliente alvo *</label>
                  <SelectCliente value={clienteOption} onChange={handleSelectCliente} isClearable />
                  {clienteSel && (
                    <div className="ssw-mini-client">
                      <div className="mc-line">
                        <span className="mc-k">Razão:</span>
                        <span className="mc-v">{clienteSel.RazaoSocial || "—"}</span>
                      </div>
                      <div className="mc-line">
                        <span className="mc-k">Fantasia:</span>
                        <span className="mc-v">{clienteSel.NomeFant || "—"}</span>
                      </div>
                      <div className="mc-line">
                        <span className="mc-k">Cód. Cad.:</span>
                        <span className="mc-v">{clienteSel.CodigoCad || "—"}</span>
                      </div>
                    </div>
                  )}
                </div>

                <div className="ssw-grid ssw-grid--2" style={{ gridColumn: "1 / -1" }}>
                  <div className="ssw-field">
                    <label>Responsável (ID)</label>
                    <Input
                      placeholder="Seu ID (opcional)"
                      value={responsavelId}
                      onChange={(e) => setResponsavelId(e.target.value)}
                    />
                  </div>
                  <div className="ssw-field">
                    <label>Responsável (nome)</label>
                    <Input value={responsavelNome || "—"} readOnly />
                  </div>
                </div>

                <div className="ssw-actions" style={{ gridColumn: "1 / -1" }}>
                  <Button onClick={handleNovaExecucao} disabled={loading || !templateId || !clienteId}>
                    Abrir execução
                  </Button>
                </div>
              </div>

              {/* Execuções em aberto do cliente */}
              {clienteId && (
                <div className="ssw-card" style={{ marginTop: 12 }}>
                  <div className="ssw-card__head">
                    <h3>Execuções em aberto do cliente</h3>
                    <span className="ssw-kpi">
                      {loadingAbertas ? "carregando…" : `${abertasCliente.length} encontradas`}
                    </span>
                  </div>

                  <div className="ssw-tablewrap">
                    <table className="ssw-table ssw-table--compact">
                      <thead>
                        <tr>
                          <th>#</th>
                          <th>Template</th>
                          <th>Início</th>
                          <th>Status</th>
                          <th>Ações</th>
                        </tr>
                      </thead>
                      <tbody>
                        {loadingAbertas && (
                          <tr>
                            <td colSpan={5} className="ssw-empty">
                              Carregando…
                            </td>
                          </tr>
                        )}
                        {!loadingAbertas && abertasCliente.length === 0 && (
                          <tr>
                            <td colSpan={5} className="ssw-empty">
                              Nenhuma execução em aberto para este cliente.
                            </td>
                          </tr>
                        )}
                        {abertasCliente.map((e) => (
                          <tr key={e.Id}>
                            <td>#{e.Id}</td>
                            <td>
                              {e.Titulo} (v{e.TemplateVersao})
                            </td>
                            <td>{e.DtInicio ? fmtDateTime(e.DtInicio) : "—"}</td>
                            <td>{e.Status}</td>
                            <td>
                              <Button variant="ghost" onClick={() => handleCarregarExecucao(e.Id)}>
                                Retomar
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Histórico recente */}
              {clienteId && (
                <div className="ssw-card" style={{ marginTop: 12 }}>
                  <div className="ssw-card__head">
                    <h3>Histórico do cliente (última por template)</h3>
                    <span className="ssw-kpi">
                      {loadingHistorico ? "carregando…" : `${historicoCliente.length} templates`}
                    </span>
                  </div>

                  <div className="ssw-tablewrap">
                    <table className="ssw-table ssw-table--compact">
                      <thead>
                        <tr>
                          <th>Template</th>
                          <th>Fim</th>
                          <th>Status aprovação</th>
                          <th>Restrições</th>
                          <th>#Exec</th>
                        </tr>
                      </thead>
                      <tbody>
                        {loadingHistorico && (
                          <tr>
                            <td colSpan={5} className="ssw-empty">
                              Carregando…
                            </td>
                          </tr>
                        )}
                        {!loadingHistorico && historicoCliente.length === 0 && (
                          <tr>
                            <td colSpan={5} className="ssw-empty">
                              Sem histórico para este cliente.
                            </td>
                          </tr>
                        )}
                        {historicoCliente.map((h) => (
                          <tr key={`${h.TemplateId}-${h.Id}`}>
                            <td>{h.Titulo}</td>
                            <td>{h.DtFim ? fmtDateTime(h.DtFim) : "—"}</td>
                            <td>
                              <span
                                className={`ssw-badge ${h.AprovacaoStatus === "APROVADO"
                                  ? "is-green"
                                  : h.AprovacaoStatus === "APROV_RESTRICAO"
                                    ? "is-blue"
                                    : ""
                                  }`}
                              >
                                {h.AprovacaoStatus || "—"}
                              </span>
                            </td>
                            <td>{h.QtdeRestricoes ?? 0}</td>
                            <td>#{h.Id}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </>
          )}

          {!!execId && (
            <div className="ssw-grid ssw-grid--3">
              <div className="ssw-field">
                <label>Execução atual</label>
                <Input readOnly value={`#${execId}`} />
              </div>
              <div className="ssw-field">
                <label>Status</label>
                <Input readOnly value={status} />
              </div>
              <div className="ssw-field">
                <label>Cliente (Cód. Cad.)</label>
                <Input readOnly value={String(clienteId || "")} />
              </div>

              {clienteSel && (
                <div className="ssw-field" style={{ gridColumn: "1 / -1" }}>
                  <div className="ssw-mini-client">
                    <div className="mc-line">
                      <span className="mc-k">Razão:</span>
                      <span className="mc-v">{clienteSel.RazaoSocial || "—"}</span>
                    </div>
                    <div className="mc-line">
                      <span className="mc-k">Fantasia:</span>
                      <span className="mc-v">{clienteSel.NomeFant || "—"}</span>
                    </div>
                    <div className="mc-line">
                      <span className="mc-k">Cód. Cad.:</span>
                      <span className="mc-v">{clienteSel.CodigoCad || "—"}</span>
                    </div>
                  </div>
                </div>
              )}

              <div className="ssw-actions" style={{ gridColumn: "1 / -1" }}>
                <Button variant="secondary" onClick={handleSalvarRascunho} disabled={loading || locked}>
                  Salvar rascunho
                </Button>
                <Button variant="ghost" onClick={handlePausarRetomar} disabled={loading || locked}>
                  {status === "PAUSADO" ? "Retomar" : "Pausar"}
                </Button>
              </div>
            </div>
          )}

          {/* Fallback - retomar por ID */}
          {!execId && (
            <div className="ssw-grid ssw-grid--3" style={{ marginTop: 8 }}>
              <div className="ssw-field">
                <label>Retomar execução (ID)</label>
                <div style={{ display: "flex", gap: 8 }}>
                  <Input
                    placeholder="Ex.: 1024"
                    value={execIdInput}
                    onChange={(e) => setExecIdInput(e.target.value)}
                  />
                  <Button
                    variant="ghost"
                    onClick={() => {
                      const n = Number(execIdInput);
                      if (Number.isFinite(n) && n > 0) handleCarregarExecucao(n);
                      else showError("Informe um ID válido.");
                    }}
                  >
                    Carregar
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Perguntas */}
        {execId && (
          <div className="ssw-card">
            <div className="ssw-card__head">
              <h2>Perguntas</h2>
              <div className="ssw-kpi">
                {respondidas.length}/{perguntasRespondiveis.length} respondidas
              </div>
            </div>
            <div className="ssw-tablewrap">
              <table className="ssw-table ssw-table--compact">
                <thead>
                  <tr>
                    <th className="w-8">#</th>
                    <th style={{ minWidth: 260 }}>Pergunta</th>
                    <th className="w-16 text-center">Obrigatória</th>
                    <th className="w-28">Resposta</th>
                    <th className="w-56">Comentário (opcional)</th>
                  </tr>
                </thead>
                <tbody>
                  {perguntas.length === 0 && (
                    <tr>
                      <td colSpan={5} className="ssw-empty">
                        Sem perguntas.
                      </td>
                    </tr>
                  )}
                  {perguntas.map((p) => {
                    if (isSectionQuestion(p)) {
                      const tituloSecao = getSectionTitle(p);

                      return (
                        <tr key={`secao-${p.Id}`}>
                          <td
                            colSpan={5}
                            style={{
                              padding: "12px 14px",
                              background: "#eef4ff",
                              borderTop: "1px solid #bfdbfe",
                              borderBottom: "1px solid #bfdbfe",
                              borderLeft: "4px solid #2563eb",
                              fontWeight: 800,
                              fontSize: 14,
                              color: "#1e3a8a",
                              textTransform: "uppercase",
                              letterSpacing: ".03em",
                            }}
                          >
                            {tituloSecao || "Seção"}
                          </td>
                        </tr>
                      );
                    }

                    const r = respostas[p.Id] || { bool: null, comentario: "" };
                    const disabled = locked || status === "PAUSADO";
                    const pid = p.Id;
                    const numeroPergunta = ordemVisualPorId.get(p.Id) ?? p.Ordem;
                    const imgs = imagens[pid] || [];
                    const isListBusy = !!listBusy[pid];
                    const isUpBusy = !!uploadBusy[pid];

                    return (
                      <React.Fragment key={p.Id}>
                        <tr>
                          <td>{numeroPergunta}</td>
                          <td>{p.Enunciado}</td>
                          <td className="text-center">{p.Obrigatoria ? "Sim" : "Não"}</td>
                          <td>
                            <Select
                              disabled={disabled}
                              value={r.bool === null ? "" : String(r.bool)}
                              onChange={(e) =>
                                setRespLocal(p.Id, { bool: normalizeBool(e.target.value) })
                              }
                            >
                              {yesNo.map((o) => (
                                <option key={o.v} value={o.v}>
                                  {o.label}
                                </option>
                              ))}
                            </Select>
                          </td>
                          <td>
                            <Textarea
                              disabled={disabled}
                              placeholder="Observações, anexos referenciados, etc."
                              value={r.comentario}
                              onChange={(e) =>
                                setRespLocal(p.Id, { comentario: e.target.value })
                              }
                            />
                          </td>
                        </tr>

                        {/* Linha de anexos por pergunta */}
                        <tr className="ssw-row-sub">
                          <td></td>
                          <td colSpan={4}>
                            <div className="ssw-attachments">
                              <div className="ssw-attachments__head">
                                <strong>Anexos da pergunta #{numeroPergunta}</strong>
                                <div className="ssw-attachments__actions">
                                  <input
                                    type="file"
                                    accept="image/jpeg,image/png,image/webp"
                                    multiple
                                    disabled={disabled || isUpBusy}
                                    onChange={(e) => {
                                      const files = e.target.files;
                                      if (!files || files.length === 0) return;
                                      uploadImagensPergunta(pid, files);
                                      e.target.value = "";
                                    }}
                                  />
                                  <Button
                                    variant="ghost"
                                    disabled={disabled || isListBusy}
                                    onClick={() => carregarImagensPergunta(pid)}
                                  >
                                    {isListBusy ? "Atualizando…" : "Atualizar lista"}
                                  </Button>
                                </div>
                              </div>

                              <div className="ssw-attachments__grid">
                                {isListBusy && imgs.length === 0 && (
                                  <div className="ssw-empty">Carregando…</div>
                                )}
                                {!isListBusy && imgs.length === 0 && (
                                  <div className="ssw-empty">Sem anexos.</div>
                                )}
                                {imgs.map((it) => (
                                  <div key={it.Id} className="ssw-attachments__item">
                                    <ImgThumb
                                      item={it}
                                      onDelete={() => {
                                        if (delBusy[it.Id]) return;
                                        excluirImagem(pid, it.Id);
                                      }}
                                    />
                                  </div>
                                ))}
                              </div>

                              <div className="ssw-hint">
                                Tipos permitidos: JPG, PNG, WEBP. Tamanho máx: 10MB por arquivo, até 10 por envio.
                              </div>
                            </div>
                          </td>
                        </tr>
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="ssw-stickybar">
              <div className="ssw-stickybar__left">
                <span className="ssw-badge is-blue">
                  {respondidas.length}/{perguntasRespondiveis.length} respondidas
                </span>
                <span className="ssw-badge is-gray">{pendentesObrig.length} obrigatórias pendentes</span>
              </div>
              <div className="ssw-stickybar__right">
                <Button
                  variant="secondary"
                  onClick={handleSalvarRascunho}
                  disabled={loading || locked || status === "PAUSADO"}
                >
                  Salvar rascunho
                </Button>
                <Button variant="ghost" onClick={handlePausarRetomar} disabled={loading || locked}>
                  {status === "PAUSADO" ? "Retomar" : "Pausar"}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Assinatura / Encerramento */}
        {execId && (
          <div className="ssw-card">
            <div className="ssw-card__head">
              <h2>Assinatura / Encerramento</h2>
            </div>

            {assinatura ? (
              <div className="ssw-field">
                <label>Status</label>
                <Input
                  readOnly
                  value={`Assinado por ${assinanteNomeExibicao} em ${fmtDateTime(
                    assinatura.AssinadaEm
                  )}`}
                />
              </div>
            ) : (
              <>
                <div className="ssw-grid ssw-grid--2">
                  <div className="ssw-field">
                    <label>Responsável (ID)</label>
                    <Input
                      placeholder="ID do usuário assinante"
                      value={responsavelId}
                      onChange={(e) => setResponsavelId(e.target.value)}
                      disabled={locked}
                    />
                  </div>
                  <div className="ssw-field">
                    <label>Responsável (nome)</label>
                    <Input value={responsavelNome || "—"} readOnly />
                  </div>
                </div>

                <div className="ssw-field" style={{ flexDirection: "row", alignItems: "center", gap: 8 }}>
                  <input
                    id="aceiteTermo"
                    type="checkbox"
                    checked={aceite}
                    onChange={(e) => setAceite(e.target.checked)}
                    disabled={locked || status === "PAUSADO"}
                  />
                  <label htmlFor="aceiteTermo">
                    Declaro que revisei todas as respostas e assumo responsabilidade pelas informações prestadas.
                  </label>
                </div>

                <div className="ssw-actions">
                  <Button
                    onClick={handleAssinar}
                    disabled={loading || locked || status === "PAUSADO" || !aceite}
                  >
                    Assinar e concluir
                  </Button>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </MainLayout>
  );
}
